import { Icon } from './Icon';

const steps = [
  ['01', '选方法', '按要研究的事情选择术数。每个模块都会标明采用的来源。'],
  ['02', '填信息', '填写时间和必要参数，然后点击“生成排盘”。'],
  ['03', '看解读', '所有设备都能免费解读；也可以按设备选择本地模型或 API。']
];

export function Guide() {
  return (
    <div className="guide-page page-shell">
      <header className="page-hero">
        <span className="eyebrow">怎么使用易鉴</span>
        <h1 tabIndex={-1}>先看排盘，再用适合当前设备的解读方式。</h1>
        <p>手机无需下载模型也能免费解读；电脑可以使用本地模型；API 始终只是可选项。</p>
      </header>

      <section className="guide-steps" aria-labelledby="guide-flow-title">
        <div className="section-heading"><span className="eyebrow">使用流程</span><h2 id="guide-flow-title">三步完成一次排盘</h2></div>
        <ol>{steps.map(([number, title, text]) => <li key={number}><span>{number}</span><div><h3>{title}</h3><p>{text}</p></div></li>)}</ol>
      </section>

      <div className="guide-columns">
        <section className="guide-card privacy-card">
          <span className="guide-icon"><Icon name="key" /></span>
          <h2>API 设置可以留在当前设备</h2>
          <ul>
            <li><Icon name="check" size={17} />默认可在当前浏览器加密记住，切换术数或刷新后不用重填。</li>
            <li><Icon name="check" size={17} />不写入 localStorage、Cookie、URL、日志或本站服务器。</li>
            <li><Icon name="check" size={17} />远程接口必须使用 HTTPS；本机地址可用 HTTP。</li>
            <li><Icon name="check" size={17} />请求前展示完整发送目标，可随时清除此设备保存。</li>
          </ul>
          <p>换浏览器、清理站点数据或更换设备后需要重新填写。公共电脑请关闭“记住设置”。你主动发送给第三方模型的数据，会按对方的隐私政策处理。</p>
        </section>
        <section className="guide-card privacy-card image-guide-card">
          <span className="guide-icon"><Icon name="book" /></span>
          <h2>图片会发出去吗？</h2>
          <ul>
            <li><Icon name="check" size={17} />图片默认只在当前页面内存中预览，不写入本地存储。</li>
            <li><Icon name="check" size={17} />仅支持 JPG、PNG、WebP，单张不超过 6 MB。</li>
            <li><Icon name="check" size={17} />请勿上传他人图片，除非已取得明确同意。</li>
            <li><Icon name="check" size={17} />浏览器本地免费模型当前只支持文字，图片不会自动发送。</li>
          </ul>
          <p>图片研究只用于传统文本对照和可见结构描述，不用于识别身份、判断健康或作现实决策。</p>
        </section>
        <section className="guide-card boundary-card">
          <span className="guide-icon"><Icon name="shield" /></span>
          <h2>哪些事不要只看排盘</h2>
          <ul>
            <li>不保证预测准确，不使用恐吓性断语。</li>
            <li>不提供“改命、消灾、必中、包成功”等承诺。</li>
            <li>不替代医生、律师、会计师、理财顾问等专业意见。</li>
            <li>不建议以术数结果独立决定婚姻、生育、投资、用药或人身安全事项。</li>
          </ul>
          <p>遇到高影响问题，请回到现实证据、专业资质与可验证行动。</p>
        </section>
      </div>

      <section className="guide-card browser-guide-card" aria-labelledby="browser-model-title">
        <span className="guide-icon"><Icon name="spark" /></span>
        <h2 id="browser-model-title">自动适配手机、平板和电脑</h2>
        <p>默认选择“自动适配”即可。网站先判断当前设备，再使用最稳妥的解读方式。</p>
        <ol className="browser-guide-steps">
          <li><strong>手机</strong><span>默认使用免费规则解读，不下载模型、不需要 API，支持连续追问。</span></li>
          <li><strong>电脑</strong><span>有 WebGPU 时可用约 350 MB 的轻量模型；条件不足自动回到免费规则解读。</span></li>
          <li><strong>增强</strong><span>性能较好的电脑可手动选择约 1 GB 的优质模型，或填写自己的 API Key。</span></li>
        </ol>
        <details className="compatibility-guide">
          <summary>各端具体怎么选？</summary>
          <p>手机和平板不用关心 WebGPU，自动模式不会下载模型。普通电脑优先尝试 Qwen3 0.6B；性能较好的电脑可手动选择 Qwen3 1.7B。任何本地模型加载失败时，都可以立即切回免费规则解读，排盘和追问不会被阻断。</p>
        </details>
      </section>

      <section className="guide-card export-guide-card" aria-labelledby="export-guide-title">
        <span className="guide-icon"><Icon name="arrow" /></span>
        <h2 id="export-guide-title">结果导出与分享</h2>
        <ul>
          <li><Icon name="check" size={17} />导出文字：保存完整结果、出处、提醒和原始数据。</li>
          <li><Icon name="check" size={17} />导出 PDF：在打印窗口选择“另存为 PDF”。</li>
          <li><Icon name="check" size={17} />生成分享链接：同时得到网址和二维码，不包含 API Key、图片或原始数据。</li>
          <li><Icon name="check" size={17} />拿到链接的人都能看到其中的问题和盘面摘要。</li>
        </ul>
        <p>分享前请删掉姓名、电话、住址等隐私信息。需要保留完整技术细节时，请使用文字导出。</p>
      </section>

      <section className="layer-diagram" aria-labelledby="layer-title">
        <div className="section-heading"><span className="eyebrow">结果怎么看</span><h2 id="layer-title">结果分三层，别混着看</h2></div>
        <div className="layers">
          <article><span>01</span><h3>排盘结果</h3><p>输入、历法、四柱、卦爻和宫位等程序计算结果。</p><strong>可以重复计算</strong></article>
          <i aria-hidden="true">→</i>
          <article><span>02</span><h3>出处与规则</h3><p>列出采用的古籍、术数专书和流派边界。</p><strong>可以查来源</strong></article>
          <i aria-hidden="true">→</i>
          <article><span>03</span><h3>辅助解读</h3><p>可使用免费规则、本地模型或自己的 API，始终控制在简短回答内。</p><strong>可以选择</strong></article>
        </div>
      </section>

      <section className="open-source-note">
        <Icon name="book" />
        <div><h2>开源与许可证</h2><p>本站采用 MIT 许可证。主要算法依赖 mingyu-core 0.1.28 和 iztro 2.6.0，第三方版权与署名见仓库中的 NOTICE。</p></div>
      </section>
    </div>
  );
}
